'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface FooterLink {
  label: string;
  href: string;
}

interface SocialLink {
  name: string;
  icon: string;
  href: string;
}

const FooterSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState<number | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear());
  }, []);

  const quickLinks = [
    { label: 'الرئيسية', labelEn: 'Home', href: '/homepage' },
    { label: 'من نحن', labelEn: 'About Us', href: '/about-us' },
    { label: 'بطاقات الاشتراك', labelEn: 'Subscription Cards', href: '/subscription-cards' },
    { label: 'شبكة الشركاء', labelEn: 'Partners Network', href: '/partners-network' },
    { label: 'اتصل بنا', labelEn: 'Contact Us', href: '/contact-us' }
  ];

  const socialLinks: SocialLink[] = [
    { name: 'تويتر', icon: 'AtSymbolIcon', href: '#' },
    { name: 'إنستغرام', icon: 'CameraIcon', href: '#' },
    { name: 'فيسبوك', icon: 'UserGroupIcon', href: '#' },
    { name: 'واتساب', icon: 'ChatBubbleLeftRightIcon', href: '#' }
  ];

  if (!isHydrated) {
    return (
      <footer className="bg-gradient-to-br from-primary via-secondary to-primary text-white py-12">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-white/20 rounded-full animate-pulse mx-auto"></div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-gradient-to-br from-primary via-secondary to-primary text-white">
      <div className="container mx-auto px-6 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="relative w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                <img
                  src="/assets/images/WhatsApp_Image_2026-07-21_at_11.25.41_AM-1784650017379.jpeg"
                  alt="شعار إبداع الحرفة للتسويق"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold font-amiri">{t('إبداع الحرفه', 'CREATIVE CRAFT')}</h3>
                <p className="text-sm opacity-80">{t('للتسويق', 'Marketing')}</p>
              </div>
            </div>
            <p className="text-white/80 leading-relaxed mb-4">
              {t(
                'منصة رائدة في مجال بطاقات الاشتراك والخصومات في المملكة العربية السعودية. نربط بين العملاء وأفضل الشركاء التجاريين لتوفير تجربة تسوق استثنائية.',
                'A leading platform for subscription cards and discounts in Saudi Arabia. We connect customers with the best business partners for an exceptional shopping experience.'
              )}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold font-amiri mb-4">{t('روابط سريعة', 'Quick Links')}</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-white/80 hover:text-accent transition-premium inline-flex items-center gap-2"
                  >
                    <Icon name="ChevronLeftIcon" size={16} />
                    <span>{t(link.label, link.labelEn)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold font-amiri mb-4">{t('تواصل معنا', 'Contact Us')}</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Icon name="PhoneIcon" size={20} className="text-accent flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm opacity-80">{t('الهاتف', 'Phone')}</p>
                  <a href="tel:+966590041741" className="text-white hover:text-accent transition-premium data-text">
                    +966590041741
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Icon name="EnvelopeIcon" size={20} className="text-accent flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm opacity-80">{t('البريد الإلكتروني', 'Email')}</p>
                  <a href="mailto:info@ibdaa-elherfa.com" className="text-white hover:text-accent transition-premium">
                    info@ibdaa-elherfa.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Icon name="MapPinIcon" size={20} className="text-accent flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm opacity-80">{t('العنوان', 'Address')}</p>
                  <p className="text-white/90">{t('خميس مشيط - حي الخالدية', 'Khamis Mushait - Al Khalidiyah')}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/70">
              © {currentYear} {t('إبداع الحرفة للتسويق. جميع الحقوق محفوظة.', 'Ibdaa Al Herfa for Marketing. All rights reserved.')}
            </p>
            <div className="flex gap-6 text-sm">
              <Link href="#" className="text-white/70 hover:text-accent transition-premium">
                {t('سياسة الخصوصية', 'Privacy Policy')}
              </Link>
              <Link href="#" className="text-white/70 hover:text-accent transition-premium">
                {t('الشروط والأحكام', 'Terms & Conditions')}
              </Link>
            </div>
          </div>
          {/* Logo at the very bottom */}
          <div className="flex justify-center mt-8 pb-2">
            <div className="relative w-24 h-24 rounded-2xl overflow-hidden shadow-xl border-2 border-accent/60">
              <img
                src="/assets/images/WhatsApp_Image_2026-07-21_at_11.25.41_AM-1784650017379.jpeg"
                alt="شعار إبداع الحرفة للتسويق"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;